/**
 * Pure helpers + the `RefundCauseCode` taxonomy for the client-side
 * RefundCausePicker. Mirrors the server-side equivalents in
 * `apps/ordo/server/src/managers/refund-manager.ts` so both ends of
 * the wire agree on the enum + the labels.
 *
 * When the canonical Prisma schema lands and the GraphQL enum is the
 * single source of truth, this file collapses to a thin re-export.
 * Until then, hand-keep the two lists in sync.
 */

export const REFUND_CAUSE_CODES_ORDERED = [
  'VENDOR_QUALITY',
  'VENDOR_SHORT_SHIP',
  'VENDOR_MISSED_DELIVERY',
  'VENDOR_OTHER',
  'ORDO_REFUND',
] as const

export type RefundCauseCode = (typeof REFUND_CAUSE_CODES_ORDERED)[number]

export const VENDOR_REFUND_CAUSE_CODES = [
  'VENDOR_QUALITY',
  'VENDOR_SHORT_SHIP',
  'VENDOR_MISSED_DELIVERY',
  'VENDOR_OTHER',
] as const

export type ClawbackReasonCode = (typeof VENDOR_REFUND_CAUSE_CODES)[number]

/** Default cancellation window when the vendor doesn't have one set. */
export const DEFAULT_CANCELLATION_WINDOW_HOURS = 48

export function isVendorCause(cause: RefundCauseCode): cause is ClawbackReasonCode {
  return cause !== 'ORDO_REFUND'
}

export function humanRefundCause(cause: RefundCauseCode): string {
  switch (cause) {
    case 'VENDOR_QUALITY':
      return 'Vendor quality'
    case 'VENDOR_SHORT_SHIP':
      return 'Vendor short ship'
    case 'VENDOR_MISSED_DELIVERY':
      return 'Vendor missed delivery'
    case 'VENDOR_OTHER':
      return 'Vendor other'
    case 'ORDO_REFUND':
      return 'Ordo absorbs (policy exception)'
  }
}

/**
 * Parent-visible refund status — mirrors the manager's
 * `ParentRefundStatus` projection so the client and server agree on
 * the chip vocabulary.
 */
export type ParentRefundStatus = 'processing' | 'pending_review' | 'refunded' | 'rejected' | 'cancelled'

export type ParentRefundStatusTone = 'default' | 'secondary' | 'success' | 'destructive' | 'warning' | 'info'

export function parentRefundStatusTone(status: ParentRefundStatus): ParentRefundStatusTone {
  switch (status) {
    case 'processing':
      return 'info'
    case 'pending_review':
      return 'warning'
    case 'refunded':
      return 'success'
    case 'rejected':
      return 'destructive'
    case 'cancelled':
      return 'secondary'
  }
}

export function parentRefundStatusLabel(status: ParentRefundStatus): string {
  switch (status) {
    case 'processing':
      return 'Refund processing'
    case 'pending_review':
      return 'Awaiting Ordo review'
    case 'refunded':
      return 'Refunded'
    case 'rejected':
      return 'Refund rejected'
    case 'cancelled':
      return 'Refund cancelled'
  }
}
